import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Pencil, Trash, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = [
  { v: "root", l: "Administrador" },
  { v: "mesero", l: "Mesero" },
  { v: "caja", l: "Caja" },
  { v: "taquero1", l: "Taquero · Pastor" },
  { v: "taquero2", l: "Taquero · Plancha" },
  { v: "taquero3", l: "Taquero · Barra" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // user object or "new"
  const [form, setForm] = useState({ name: "", role: "mesero", pin: "", active: true });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm({ name: "", role: "mesero", pin: "", active: true }); };
  const startEdit = (u) => { setEditing(u); setForm({ name: u.name, role: u.role, pin: "", active: u.active }); };
  const close = () => setEditing(null);

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/users", form);
        toast.success("Usuario creado");
      } else {
        const body = { ...form };
        if (!body.pin) delete body.pin;
        await api.patch(`/users/${editing.id}`, body);
        toast.success("Usuario actualizado");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-2">Usuarios</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Personal y permisos</h1>
        </div>
        <button onClick={startNew} className="btn-brand h-12 px-5 inline-flex items-center gap-2" data-testid="users-new">
          <Plus size={20} weight="bold" /> Nuevo Usuario
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="p-4">Nombre</th><th className="p-4">Rol</th><th className="p-4">Estado</th><th className="p-4 text-right">Acciones</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`users-row-${u.id}`}>
                <td className="p-4 font-semibold">{u.name}</td>
                <td className="p-4 text-sm text-zinc-600">{ROLES.find((r) => r.v === u.role)?.l || u.role}</td>
                <td className="p-4 text-sm"><span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{u.active ? "Activo" : "Inactivo"}</span></td>
                <td className="p-4 text-right">
                  <button onClick={() => startEdit(u)} className="text-zinc-500 hover:text-brand p-2" data-testid={`users-edit-${u.id}`}><Pencil size={18} weight="bold" /></button>
                  <button onClick={() => remove(u)} className="text-zinc-500 hover:text-red-500 p-2 ml-1" data-testid={`users-delete-${u.id}`}><Trash size={18} weight="bold" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-xl font-bold">{editing === "new" ? "Nuevo usuario" : "Editar usuario"}</div>
              <button onClick={close} className="text-zinc-400 hover:text-red-500"><X size={22} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Nombre</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="users-form-name" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Rol</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none" data-testid="users-form-role">
                  {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">PIN (4-6 dígitos) {editing !== "new" && <span className="text-zinc-400 normal-case font-normal">· dejar vacío para mantener</span>}</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} className="w-full h-12 px-3 rounded-xl border-2 border-zinc-200 focus:border-brand outline-none font-mono text-lg" data-testid="users-form-pin" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <span className="text-sm">Activo</span>
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={close} className="flex-1 h-12 rounded-xl bg-zinc-100 font-semibold">Cancelar</button>
              <button onClick={save} className="flex-1 h-12 rounded-xl btn-brand font-semibold" data-testid="users-form-save">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
